import { useEffect, useState } from "react";
import { Timer } from "lucide-react";
import { useApi, fmtDate } from "@/lib/api";

const f = (x, d = 2) => (x == null ? "—" : Number(x).toFixed(d));

const RunRow = ({ x }) => (
  <tr className="border-t border-white/5">
    <td className="py-1 pr-2 whitespace-nowrap">{fmtDate(x.date)}</td>
    <td className="pr-2 whitespace-nowrap">{x.track} R{x.race_number} · ~{x.timed_distance}m</td>
    <td className="pr-2 whitespace-nowrap">{x.condition || "—"}</td>
    <td className="pr-2">{x.finish ? `${x.finish}/${x.field_size}` : x.did_not_finish || "—"}</td>
    <td className="pr-2 font-mono">{(x.positions_every_200m || []).some((p) => p) ? x.positions_every_200m.map((p) => p ?? "·").join("-") : "—"}</td>
    <td className="pr-2 font-mono">{f(x.l600)}{x.l600_rank ? ` (${x.l600_rank})` : ""}</td>
    <td className="pr-2 font-mono">{f(x.l200)}{x.l200_rank ? ` (${x.l200_rank})` : ""}</td>
    <td className="font-mono">{x.extra_ground == null ? "—" : `${x.extra_ground > 0 ? "+" : ""}${x.extra_ground}m`}</td>
  </tr>
);

export const SectionalsPanel = ({ meetingId, race }) => {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api("get", `/admin/meetings/${meetingId}/races/${race.number}/sectionals`).then(setD).catch(() => setD({ runners: {}, no_qld_sectionals: [] })); }, [api, meetingId, race.number]);
  if (!d) return null;
  const names = Object.fromEntries(race.runners.map((r) => [String(r.number), r.horse]));
  const nums = Object.keys(d.runners);

  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/10 p-5" data-testid="sectionals-panel">
      <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Timer className="w-4 h-4 text-emerald-400" />QLD sectionals (admin only)</h3>
      <p className="text-xs text-slate-500 mb-3">Official Racing Queensland timing from each horse's past Queensland starts. Brackets show rank among finishers (1 = fastest). On a phone, swipe the table sideways. The AI uses this too.</p>
      {nums.length === 0 && <p className="text-sm text-slate-400" data-testid="sectionals-empty">No Queensland sectionals stored for this field yet.</p>}
      <div className="space-y-4">
        {nums.map((n) => (
          <div key={n} data-testid={`sectionals-runner-${n}`}>
            <p className="text-sm font-bold mb-1">{n}. {names[n]}</p>
            <div className="overflow-x-auto">
              <table className="text-[11px] text-slate-300 min-w-[640px]">
                <thead className="text-slate-500"><tr><th className="text-left pr-2">Date</th><th className="text-left pr-2">Track · race · timed distance</th><th className="text-left pr-2">Going</th><th className="text-left pr-2">Fin</th><th className="text-left pr-2">Positions /200m</th><th className="text-left pr-2">L600</th><th className="text-left pr-2">L200</th><th className="text-left">Extra ground</th></tr></thead>
                <tbody>{d.runners[n].map((x, i) => <RunRow key={i} x={x} />)}</tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      {d.no_qld_sectionals.length > 0 && <p className="text-xs text-slate-500 mt-3" data-testid="sectionals-missing">No Queensland sectionals: {d.no_qld_sectionals.map((n) => `${n}. ${names[n]}`).join(", ")}</p>}
    </section>
  );
};
